// Surveys 2.0 step analytics (spec "Analytics").
//
// Collects survey_shown / survey_resumed / welcome_viewed / welcome_started /
// step_viewed / step_answered /
// step_skipped / step_back / survey_closed / survey_completed for the response
// on screen and sends them in batches to POST {apiUrl}/surveys/:id/events:
// every 15 s, as soon as 10 are queued on a step change, on completion, and
// with keepalive on close / pagehide / hidden tab. Never blocks the UI: a
// batch that fails twice more is dropped. step_viewed is sent once per step per response. No answer values,
// only the numeric score of scale questions.
//
// It also reports `survey-step-viewed` to the host (JS SDK), which forwards
// survey events to the site's GA4 / GTM / Meta Pixel when that is turned on.
import CommunicationManager from "./CommunicationManager";
import { useSessionStore } from "../Stores/Session";
import type { SurveyAnswerValue, SurveyBlock, SurveyChannel, SurveyDefinition } from "./surveyTypes";

type EventType =
  | "survey_shown"
  | "survey_resumed"
  | "welcome_viewed"
  | "welcome_started"
  | "step_viewed"
  | "step_answered"
  | "step_skipped"
  | "step_back"
  | "survey_closed"
  | "survey_completed";

interface SurveyEvent {
  type: EventType;
  ts: number;
  blockId?: string;
  stepIndex?: number;
  msOnStep?: number;
  score?: number;
  status?: string;
  endingId?: string;
  durationMs?: number;
}

export interface SurveyAnalyticsContext {
  surveyId: string;
  version: number;
  responseId: string;
  channel: SurveyChannel;
  language?: string;
  definition: SurveyDefinition;
  pageUrl?: string;
  referrer?: string;
  sdkVersion?: string;
  /** Forward to the site's analytics (GA4 / GTM / Meta Pixel) via the JS SDK. */
  forward?: boolean;
  surveyName?: string;
  resumed?: boolean;
}

// A 5-question survey answered in a minute sends ~5 batches, not ~13 (5 s).
// Nothing waits for them: the dashboard caches analytics for 60 s, and close,
// pagehide and a hidden tab flush right away.
const FLUSH_INTERVAL_MS = 15000;
const FLUSH_AT = 10;
const MAX_BATCH = 50;
const RETRIES = 2;
const SCORE_TYPES = new Set(["nps", "csat", "rating", "scale"]);
const EVENT_CHANNELS = new Set(["web", "ios", "android", "page", "email", "embed"]);

// ── Device / source ─────────────────────────────────────────────────────────

export type DeviceClass = "phone" | "tablet" | "desktop";

export const deviceClassOf = (ua: string, touchPoints = 0): DeviceClass => {
  if (/iPad|Tablet|PlayBook|Silk|Kindle/i.test(ua)) return "tablet";
  // iPadOS reports a desktop Mac user agent.
  if (/Macintosh/i.test(ua) && touchPoints > 1) return "tablet";
  if (/Android/i.test(ua) && !/Mobile/i.test(ua)) return "tablet";
  if (/Mobi|iPhone|iPod|Android|Windows Phone/i.test(ua)) return "phone";
  return "desktop";
};

export const osOf = (ua: string, touchPoints = 0): string => {
  if (/iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && touchPoints > 1)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/CrOS/i.test(ua)) return "chromeos";
  if (/Macintosh|Mac OS X/i.test(ua)) return "macos";
  if (/Windows/i.test(ua)) return "windows";
  if (/Linux/i.test(ua)) return "linux";
  return "other";
};

/** `{ class, os }` of this device (also stored on the response). */
export const surveyDeviceInfo = (): { class: DeviceClass; os: string } => {
  try {
    const ua = navigator.userAgent || "";
    const touch = navigator.maxTouchPoints || 0;
    return { class: deviceClassOf(ua, touch), os: osOf(ua, touch) };
  } catch (exp) {
    return { class: "desktop", os: "other" };
  }
};

/** Path + UTM parameters of the host page; referrer reduced to its host. */
export const surveySourceOf = (
  pageUrl?: string,
  referrer?: string,
): { pagePath?: string; source: Record<string, string> } => {
  const source: Record<string, string> = {};
  let pagePath: string | undefined;
  let pageHost = "";
  try {
    if (pageUrl) {
      const url = new URL(pageUrl);
      pagePath = url.pathname.slice(0, 200);
      pageHost = url.hostname;
      const utm = (name: string) => url.searchParams.get(name)?.trim().slice(0, 150);
      const utmSource = utm("utm_source");
      const utmMedium = utm("utm_medium");
      const utmCampaign = utm("utm_campaign");
      if (utmSource) source.utmSource = utmSource;
      if (utmMedium) source.utmMedium = utmMedium;
      if (utmCampaign) source.utmCampaign = utmCampaign;
    }
  } catch (exp) {}
  try {
    if (referrer) {
      const host = new URL(referrer).hostname.replace(/^www\./, "");
      // Navigation inside the same site is not a source.
      if (host && host !== pageHost.replace(/^www\./, "")) source.referrer = host;
    }
  } catch (exp) {}
  return { pagePath, source };
};

const VIEWER_KEY = "gleap-survey-viewer";
const BASE62 = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz";

/** Anonymous per-browser id for unique views when there is no session. */
const viewerId = (): string | undefined => {
  try {
    const existing = localStorage.getItem(VIEWER_KEY);
    if (existing && /^vw_[A-Za-z0-9]{8,40}$/.test(existing)) return existing;
    let id = "vw_";
    for (let i = 0; i < 16; i++) id += BASE62[Math.floor(Math.random() * 62)];
    localStorage.setItem(VIEWER_KEY, id);
    return id;
  } catch (exp) {
    return undefined;
  }
};

// ── Sending ─────────────────────────────────────────────────────────────────

interface Batch {
  surveyId: string;
  body: Record<string, any>;
}

const headers = (): Record<string, string> | null => {
  const { sdkKey, session } = useSessionStore.getState();
  if (!sdkKey) return null;
  const result: Record<string, string> = { "Content-Type": "application/json", "Api-Token": sdkKey };
  if (session) {
    result["Gleap-Id"] = session.gleapId ?? "";
    result["Gleap-Hash"] = session.gleapHash ?? "";
  }
  return result;
};

// 2 s, then 4 s, each with up to 2 s of jitter (no synchronized retry wave).
const retryDelay = (attempt: number) => 2000 * (attempt + 1) + Math.round(Math.random() * 2000);

const postBatch = (batch: Batch, keepalive: boolean, attempt = 0) => {
  const apiUrl = useSessionStore.getState().apiUrl;
  const requestHeaders = headers();
  if (!apiUrl || !requestHeaders) return;
  try {
    fetch(`${apiUrl}/surveys/${encodeURIComponent(batch.surveyId)}/events`, {
      method: "POST",
      headers: requestHeaders,
      body: JSON.stringify(batch.body),
      credentials: "omit",
      keepalive,
    })
      .then((response) => {
        response.text().catch(() => {});
        // 4xx (unknown survey, invalid batch) never succeeds on retry.
        if ((response.status >= 500 || response.status === 429) && !keepalive && attempt < RETRIES) {
          setTimeout(() => postBatch(batch, false, attempt + 1), retryDelay(attempt));
        }
      })
      .catch(() => {
        if (!keepalive && attempt < RETRIES) {
          setTimeout(() => postBatch(batch, false, attempt + 1), retryDelay(attempt));
        }
      });
  } catch (exp) {}
};

const hostMessage = (name: string, data: any) => {
  try {
    CommunicationManager.getInstance().sendMessage({ name, data });
  } catch (exp) {}
};

// ── Tracker ─────────────────────────────────────────────────────────────────

class SurveyAnalyticsTracker {
  private ctx: (SurveyAnalyticsContext & { pagePath?: string; source: Record<string, string>; device: { class: DeviceClass; os: string } }) | null = null;
  private queue: SurveyEvent[] = [];
  private viewed = new Set<string>();
  private currentBlockId: string | null = null;
  private stepStartedAt = 0;
  private shownAt = 0;
  private timer: any = null;
  private lifecycleBound = false;
  private ended = false;

  /** A survey is on screen (new or resumed response). */
  begin(context: SurveyAnalyticsContext) {
    this.flush(false);
    const { pagePath, source } = surveySourceOf(context.pageUrl, context.referrer);
    this.ctx = { ...context, pagePath, source, device: surveyDeviceInfo() };
    this.queue = [];
    this.viewed = new Set();
    this.currentBlockId = null;
    this.ended = false;
    this.shownAt = Date.now();
    this.push({ type: "survey_shown", ts: this.shownAt });
    if (context.resumed) this.push({ type: "survey_resumed", ts: this.shownAt });
    this.bindLifecycle();
    if (!this.timer) this.timer = setInterval(() => this.flush(false), FLUSH_INTERVAL_MS);
  }

  /** What the host needs to forward survey events (merged into survey-* messages). */
  hostFields(): { forward: boolean; surveyName?: string } {
    return { forward: !!this.ctx?.forward, ...(this.ctx?.forward && this.ctx.surveyName ? { surveyName: this.ctx.surveyName } : {}) };
  }

  /** Stored on the response too (device + where it came from). */
  responseExtras(): { device?: { class: string; os: string }; source?: Record<string, string> } {
    if (!this.ctx) return {};
    return { device: this.ctx.device, ...(Object.keys(this.ctx.source).length ? { source: this.ctx.source } : {}) };
  }

  /** The welcome screen is on screen (before the first question). */
  welcomeViewed() {
    if (!this.ctx || this.ended) return;
    this.push({ type: "welcome_viewed", ts: Date.now() });
  }

  /** Start pressed on the welcome screen. */
  welcomeStarted() {
    if (!this.ctx || this.ended) return;
    this.push({ type: "welcome_started", ts: Date.now() });
  }

  /** The block at the cursor; call on every state change (deduped). */
  stepShown(blockId: string | undefined) {
    const ctx = this.ctx;
    if (!ctx || this.ended || !blockId || blockId === this.currentBlockId) return;
    const index = ctx.definition.blocks.findIndex((block) => block.id === blockId);
    if (index < 0) return;
    this.currentBlockId = blockId;
    this.stepStartedAt = Date.now();
    if (this.viewed.has(blockId)) return;
    this.viewed.add(blockId);
    this.push({ type: "step_viewed", ts: this.stepStartedAt, blockId, stepIndex: index });
    // Deferred: on show the host hears survey-shown first, then the first step.
    const hostFields = this.hostFields();
    setTimeout(() => hostMessage("survey-step-viewed", {
      surveyId: ctx.surveyId,
      version: ctx.version,
      responseId: ctx.responseId,
      blockId,
      key: ctx.definition.blocks[index].key,
      stepIndex: index,
      ...hostFields,
    }), 0);
    if (this.queue.length >= FLUSH_AT) this.flush(false);
  }

  /** The step at the cursor was answered (or skipped when the value is empty). */
  answered(block: SurveyBlock, value: SurveyAnswerValue | undefined) {
    const ctx = this.ctx;
    if (!ctx || this.ended || block.type === "statement") return;
    const now = Date.now();
    const stepIndex = ctx.definition.blocks.findIndex((candidate) => candidate.id === block.id);
    const msOnStep = this.currentBlockId === block.id && this.stepStartedAt ? now - this.stepStartedAt : undefined;
    const skipped = value === undefined || value === null || (typeof value === "string" && value.trim() === "") || (Array.isArray(value) && value.length === 0);
    this.push({
      type: skipped ? "step_skipped" : "step_answered",
      ts: now,
      blockId: block.id,
      stepIndex,
      ...(msOnStep !== undefined ? { msOnStep } : {}),
      ...(!skipped && SCORE_TYPES.has(block.type) && typeof value === "number" ? { score: value } : {}),
    });
  }

  back(fromBlockId: string | undefined) {
    const ctx = this.ctx;
    if (!ctx || this.ended || !fromBlockId) return;
    const stepIndex = ctx.definition.blocks.findIndex((block) => block.id === fromBlockId);
    if (stepIndex < 0) return;
    this.push({ type: "step_back", ts: Date.now(), blockId: fromBlockId, stepIndex });
  }

  completed(endingId: string | undefined) {
    if (!this.ctx || this.ended) return;
    const now = Date.now();
    this.push({ type: "survey_completed", ts: now, ...(endingId ? { endingId } : {}), durationMs: now - this.shownAt });
    this.flush(false);
  }

  closed(blockId: string | undefined, status: string) {
    if (!this.ctx || this.ended) return;
    this.push({ type: "survey_closed", ts: Date.now(), ...(blockId ? { blockId } : {}), status });
    this.ended = true;
    this.flush(true);
    this.stopTimer();
  }

  /** The survey left the screen without a close (reset / another survey). */
  end() {
    this.flush(true);
    this.ended = true;
    this.stopTimer();
  }

  private stopTimer() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  private push(event: SurveyEvent) {
    this.queue.push(event);
    if (this.queue.length >= MAX_BATCH) this.flush(false);
  }

  flush(keepalive: boolean) {
    const ctx = this.ctx;
    if (!ctx || this.queue.length === 0) return;
    const events = this.queue.splice(0, MAX_BATCH);
    const channel = EVENT_CHANNELS.has(ctx.channel) ? ctx.channel : "web";
    postBatch(
      {
        surveyId: ctx.surveyId,
        body: {
          version: ctx.version,
          responseId: ctx.responseId,
          viewerId: viewerId(),
          channel,
          device: ctx.device.class,
          os: ctx.device.os,
          ...(ctx.sdkVersion ? { sdkVersion: ctx.sdkVersion } : {}),
          ...(ctx.language ? { language: ctx.language } : {}),
          ...(ctx.pagePath ? { pagePath: ctx.pagePath } : {}),
          ...(Object.keys(ctx.source).length ? { source: ctx.source } : {}),
          events,
        },
      },
      keepalive,
    );
    if (this.queue.length > 0) this.flush(keepalive);
  }

  private bindLifecycle() {
    if (this.lifecycleBound) return;
    this.lifecycleBound = true;
    try {
      window.addEventListener("pagehide", () => this.flush(true));
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "hidden") this.flush(true);
      });
    } catch (exp) {}
  }
}

export const surveyAnalytics = new SurveyAnalyticsTracker();
